import React from 'react';

import ReactModal, { IProps as ModalProps } from 'core/components/Modal';

import { useToggle } from 'core/hooks/useToggle';
import { isWebBrowser } from 'core/utils/global-utils';
import { useAppContext } from 'core/hooks/useAppContext';
import { MODAL_ARIA_NAMES } from 'core/constants';
import {
	OverlayCoordinatorOptions,
	useOverlayCoordinator,
} from 'core/hooks/useOverlayCoordinator';

type Modal = Omit<ModalProps, 'isOpen' | 'onRequestClose'>;

interface IUseModal extends Array<any> {
	0: React.FC<Modal>;
	1: (shouldOpen?: any) => void;
}

/**
 * Custom hook to use Modal component.
 *
 * @param initialState  - the initial open state of the Modal.
 * @returns {IUseModal} - the Modal component with its own toggle method to control the isOpen state
 * @param overlayOptions - options for coordinating the overlay with other overlays. This will not affect the internal behavior of the Modal component itself.
 */
export const useModal = (
	initialState = false,
	rootId = 'root',
	overlayOptions?: Omit<OverlayCoordinatorOptions, 'isRequested'>,
): IUseModal => {
	const [isOpen, toggleModal] = useToggle(initialState);
	const [isModalClosed, setIsModalClosed] = React.useState(false);
	const { isActiveOverlay, releaseOverlay } = useOverlayCoordinator({
		...overlayOptions,
		id: overlayOptions?.id ?? '',
		isRequested: isOpen,
	});
	const {
		handleAccountSwitcherModal,
		handleAccountSwitch,
		handleLivePersonModal,
		handleTriggerTalSsoUrl,
		handleTalSsoUrl,
	} = useAppContext();

	const Modal = React.useCallback(
		({ children, onAfterClose, ...restProps }: Modal) => (
			<ReactModal
				isOpen={isOpen && isActiveOverlay}
				onRequestClose={() => {
					toggleModal(false);
					releaseOverlay?.(overlayOptions?.id ?? '');
					handleAccountSwitcherModal('');
					handleAccountSwitch(true);
					handleLivePersonModal(false);
					// TAL SSO
					handleTriggerTalSsoUrl(false);
					handleTalSsoUrl(null);
				}}
				onAfterClose={() => {
					setIsModalClosed(true);
					onAfterClose?.();
				}}
				appElement={(isWebBrowser && document.getElementById(rootId)) ?? {}}
				ariaHideApp={isOpen && isActiveOverlay}
				aria={{
					labelledby: MODAL_ARIA_NAMES.HEADING,
					describedby: MODAL_ARIA_NAMES.DESCRIPTION,
				}}
				contentLabel={MODAL_ARIA_NAMES.HEADING}
				{...restProps}
			>
				{children}
			</ReactModal>
		),
		[
			isOpen,
			isActiveOverlay,
			toggleModal,
			releaseOverlay,
			overlayOptions?.id,
			rootId,
		],
	);

	return [Modal, toggleModal, isModalClosed];
};
